import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ErrorState, LoadingState } from './components/States.js';
import { ApiError, apiRequest } from './lib/api.js';
import type { AuthMe } from './types.js';

const ProtectedShell = lazy(() =>
  import('./components/ProtectedShell.js').then((module) => ({
    default: module.ProtectedShell,
  })),
);
const CreateTaskPage = lazy(() =>
  import('./pages/CreateTaskPage.js').then((module) => ({
    default: module.CreateTaskPage,
  })),
);
const EditAlertsPage = lazy(() =>
  import('./pages/EditAlertsPage.js').then((module) => ({
    default: module.EditAlertsPage,
  })),
);
const LoginPage = lazy(() =>
  import('./pages/LoginPage.js').then((module) => ({
    default: module.LoginPage,
  })),
);
const RecordsPage = lazy(() =>
  import('./pages/RecordsPage.js').then((module) => ({
    default: module.RecordsPage,
  })),
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage.js').then((module) => ({
    default: module.SettingsPage,
  })),
);
const TaskDetailPage = lazy(() =>
  import('./pages/TaskDetailPage.js').then((module) => ({
    default: module.TaskDetailPage,
  })),
);
const TasksPage = lazy(() =>
  import('./pages/TasksPage.js').then((module) => ({
    default: module.TasksPage,
  })),
);

function ProtectedRoute() {
  const location = useLocation();
  const auth = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => apiRequest<AuthMe>('/auth/me'),
    retry: false,
    staleTime: 60_000,
  });
  if (auth.isLoading) return <div className="app-boot"><LoadingState label="正在恢复会话" /></div>;
  if (auth.error instanceof ApiError && auth.error.status === 401) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (auth.isError) return <div className="app-boot"><ErrorState message={auth.error.message} /></div>;
  return <ProtectedShell />;
}

export function App() {
  return (
    <Suspense fallback={<div className="app-boot"><LoadingState label="正在加载页面" /></div>}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<Navigate to="/tasks" replace />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/tasks/new" element={<CreateTaskPage />} />
          <Route path="/tasks/:id" element={<TaskDetailPage />} />
          <Route path="/tasks/:id/alerts" element={<EditAlertsPage />} />
          <Route path="/records" element={<RecordsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/tasks" replace />} />
      </Routes>
    </Suspense>
  );
}
